import React, { useEffect, useState } from "react";
import {
  Table,
  TableRow,
  TableBody,
  useTheme,
  Box,
  Divider,
} from "@mui/material";
import { Tablecell } from "../../../../component/Methods";

const SummarySectionThree = ({ currentSignalsData, modifiedSignalsData }) => {
  const [CombinedArray, setCombinedArray] = useState([]);

  useEffect(() => {
    // Only show data when we have modified signals data
    if (modifiedSignalsData && modifiedSignalsData.length > 0 && currentSignalsData) {
      // Helper function to normalize ticker name (use full name to preserve uniqueness)
      const normalizeTicker = (name) => {
        if (!name) return "";
        // Use the full name (including /ASSET part) to preserve uniqueness
        // This ensures "BKR/A" and "BKR/B" are treated as different tickers
        return name.trim().toUpperCase();
      };

      // Helper function to normalize long_short value
      const normalizeLongShort = (value, existingLongShort) => {
        // Always derive from value to ensure accuracy (value is the source of truth)
        // If value is negative, it's short; if positive or zero, it's long
        const numValue = parseFloat(value) || 0;
        return numValue >= 0 ? "L" : "S";
      };

      // Create maps for easier lookup
      const currentMap = new Map();
      const modifiedMap = new Map();

      // Build current signals map
      currentSignalsData.forEach(signal => {
        const ticker = normalizeTicker(signal.name);
        if (!ticker) return;
        
        const value = parseFloat(signal.value) || 0;
        // Always derive long_short from value to ensure accuracy
        const longShort = normalizeLongShort(value, signal.long_short);
        
        currentMap.set(ticker, {
          ...signal,
          ticker,
          value: value,
          long_short: longShort,
        });
      });

      // Build modified signals map
      modifiedSignalsData.forEach(signal => {
        const ticker = normalizeTicker(signal.name);
        if (!ticker) return;
        
        const value = parseFloat(signal.value) || 0;
        // Always derive long_short from value to ensure accuracy
        const longShort = normalizeLongShort(value, signal.long_short);
        
        modifiedMap.set(ticker, {
          ...signal,
          ticker,
          value: value,
          long_short: longShort,
        });
      });

      // Find all unique tickers from both sets
      const allTickers = new Set([...currentMap.keys(), ...modifiedMap.keys()]);

      // Calculate metrics for new summary format
      let changedBLCount = 0;
      let changedBLWeight = 0;
      let changedSLCount = 0;
      let changedSLWeight = 0;
      let changedSSCount = 0;
      let changedSSWeight = 0;
      let changedCSCount = 0;
      let changedCSWeight = 0;
      let openedCount = 0;
      let openedWeight = 0;
      let closedCount = 0;
      let closedWeight = 0;

      allTickers.forEach(ticker => {
        const currentSignal = currentMap.get(ticker);
        const modifiedSignal = modifiedMap.get(ticker);

        if (modifiedSignal && Math.abs(modifiedSignal.value || 0) < 0.0001) {
          // Modified signal exists but weight is 0 - treat as CLOSED
          closedCount++;
          const currentValue = currentSignal ? parseFloat(currentSignal.value) || 0 : 0;
          closedWeight += Math.abs(currentValue); // Use absolute value for closed weight
        } else if (currentSignal && modifiedSignal) {
          // Ticker exists in both - check if values changed
          const currentValue = parseFloat(currentSignal.value) || 0;
          const modifiedValue = parseFloat(modifiedSignal.value) || 0;

          // Check if any key attributes changed
          const valueChanged = Math.abs(currentValue - modifiedValue) > 0.0001;
          const assetTypeChanged = (currentSignal.assetType || "").trim() !== (modifiedSignal.assetType || "").trim();
          const longShortChanged = (currentSignal.long_short || "").toUpperCase() !== (modifiedSignal.long_short || "").toUpperCase();

          if (valueChanged || assetTypeChanged || longShortChanged) {
            // Determine specific change type based on position direction and weight change
            const currentIsLong = currentValue >= 0;
            const modifiedIsLong = modifiedValue >= 0;
            const weightIncreased = modifiedValue > currentValue;

            if (currentIsLong && modifiedIsLong) {
              // Both positions are long
              if (weightIncreased) {
                changedBLCount++;
                changedBLWeight += (modifiedValue - currentValue);
              } else {
                changedSLCount++;
                changedSLWeight += (currentValue - modifiedValue);
              }
            } else if (!currentIsLong && !modifiedIsLong) {
              // Both positions are short
              if (weightIncreased) {
                changedCSCount++;
                changedCSWeight += (modifiedValue - currentValue);
              } else {
                changedSSCount++;
                changedSSWeight += (currentValue - modifiedValue);
              }
            } else {
              // Position direction changed - this will be handled by the generic "CHANGED" case
              // For now, we'll categorize based on the weight change direction
              if (weightIncreased) {
                if (modifiedIsLong) {
                  changedBLCount++;
                  changedBLWeight += modifiedValue;
                } else {
                  changedCSCount++;
                  changedCSWeight += Math.abs(modifiedValue);
                }
              } else {
                if (currentIsLong) {
                  changedSLCount++;
                  changedSLWeight += currentValue;
                } else {
                  changedSSCount++;
                  changedSSWeight += Math.abs(currentValue);
                }
              }
            }
          }
        } else if (!currentSignal && modifiedSignal) {
          // Only in modified signals - OPEN position
          openedCount++;
          const modifiedValue = parseFloat(modifiedSignal.value) || 0;
          openedWeight += Math.abs(modifiedValue);
        } else if (currentSignal && !modifiedSignal) {
          // Only in current signals - CLOSED position
          closedCount++;
          const currentValue = parseFloat(currentSignal.value) || 0;
          closedWeight += Math.abs(currentValue);
        }
      });

          function roundit(number) {
            return number.toFixed(2).replace(/\.?0+$/, "");
          }

          const transformedArray = [
            [
              { name: "Changed - BL", count: changedBLCount, weight: roundit(changedBLWeight) },
              { name: "Changed - SL", count: changedSLCount, weight: roundit(changedSLWeight) },
              { name: "Changed - SS", count: changedSSCount, weight: roundit(changedSSWeight) }
            ],
            [
              { name: "Changed - CS", count: changedCSCount, weight: roundit(changedCSWeight) },
              { name: "Opened", count: openedCount, weight: roundit(openedWeight) },
              { name: "Closed", count: closedCount, weight: roundit(closedWeight) }
            ]
          ];

          setCombinedArray(transformedArray);
        } else {
          setCombinedArray([]);
        }
  }, [currentSignalsData, modifiedSignalsData]);

  const theme = useTheme();
  
  // Don't render anything if there's no modified signals data
  if (!modifiedSignalsData || modifiedSignalsData.length === 0) {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          border: `100px ${theme.palette.lightGrey.tableHeader}`,
          borderRadius: "4px",
          marginTop: "10px",
          width: "100%",
          marginRight: 1,
        }}
      >
        <Table
          size="small"
          stickyHeader
          aria-label="a dense table"
          sx={{
            borderCollapse: "collapse",
            border: `1px solid ${theme.palette.divider}`,
            marginTop: "0px",
          }}
        >
          <TableBody>
            {CombinedArray?.map((row, rowIndex) => (
              <TableRow
                key={rowIndex}
                sx={{
                  "&:hover": {
                    background: theme.palette.grey[200],
                    cursor: "pointer",
                  },
                }}
              >
                {row.map((cell, cellIndex) => (
                  <Tablecell
                    key={cellIndex}
                    align="center"
                    sx={{
                      fontSize: "12px",
                      padding: "4px 4px",
                      color: theme.palette.text.primary,
                      borderRight: cellIndex < row.length - 1 ? `1px solid ${theme.palette.divider}` : "none",
                      borderLeft: "none",
                      borderTop: "none",
                      borderBottom: rowIndex === 0 ? `1px solid ${theme.palette.divider}` : "none",
                      width: "33.33%",
                    }}
                  >
                    <Box sx={{ fontWeight: "900", fontSize: "12px", marginBottom: "1px" }}>
                      {cell?.name}
                    </Box>
                    <Box sx={{ fontWeight: "500" }}>
                      {cell.count} ({cell.weight}%)
                    </Box>
                  </Tablecell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </>
  );
};
export default SummarySectionThree;
